/**
 * Normalizes assistant/chatbot message content when models return structured JSON
 * (e.g. OpenAI with { answer, sources, confidence, ... }) instead of plain text.
 */

export interface AssistantMessageSection {
  title: string;
  content: string;
  /** When set, the UI should translate the title via `aiChatbot.structured.${i18nKey}` */
  i18nKey?: 'sources' | 'confidence' | 'limitations' | 'recommendations';
}

export interface ParsedAssistantPayload {
  body: string;
  metaSections: AssistantMessageSection[];
  mode: 'raw' | 'structured';
  parser: 'none' | 'block' | 'inline' | 'json';
}

const SECTION_LABEL_PATTERN = '(?:\\*\\*|__)?([A-Za-zÀ-ÿ0-9][^:\\n]{0,80}?)(?:\\*\\*|__)?';
// A section header, either on its own line ("Sources:") or leading a line
// ("Sources: [1] ..."). Used only to peel *recognized meta* blocks (see below)
// out of an answer — never to split arbitrary prose.
const HEADER_PATTERN = new RegExp(`^\\s*(?:[-*+]\\s+)?${SECTION_LABEL_PATTERN}\\s*:\\s*$`);
const INLINE_LINE_PATTERN = new RegExp(`^\\s*(?:[-*+]\\s+)?${SECTION_LABEL_PATTERN}\\s*:\\s*(.*)$`);
// Only these section kinds are ever hidden into the details modal. Everything
// else (intro prose, "Résultat global:", "En bref:", "Par pôle:", …) is real
// answer content and MUST stay in the body. Matching a bare word like "resultat"
// used to hijack a plain summary into the modal — that was the display bug.
const SOURCE_SECTION_HINTS = ['source', 'sources', 'reference', 'references', 'citation', 'citations'];
const META_SECTION_HINTS = [
  'confidence', 'confiance',
  'limitation', 'limitations',
  'recommendation', 'recommendations', 'recommandation', 'recommandations',
];

function stripMarkdownJsonFence(raw: string): string {
  const trimmed = raw.trim();
  const fenceMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)```$/i);
  if (fenceMatch) {
    return fenceMatch[1].trim();
  }
  return trimmed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractJsonPrimaryAnswer(obj: Record<string, unknown>): string | null {
  const keys = ['answer', 'response', 'reply', 'text', 'message', 'content'];
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) {
      return v;
    }
  }
  return null;
}

function formatSourcesMarkdown(sources: unknown): string | null {
  if (sources === undefined || sources === null) return null;
  if (typeof sources === 'string') {
    const t = sources.trim();
    return t || null;
  }
  if (!isRecord(sources)) return null;
  const entries = Object.entries(sources);
  if (entries.length === 0) return null;
  return entries
    .map(([k, v]) => {
      const label = String(k).trim();
      const text = typeof v === 'string' ? v : JSON.stringify(v);
      return `- **${label}** ${text}`;
    })
    .join('\n');
}

function formatStringArrayField(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const lines = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  if (lines.length === 0) return null;
  return lines.map((line) => `- ${line}`).join('\n');
}

function tryParseJsonAssistantPayload(content: string): ParsedAssistantPayload | null {
  const stripped = stripMarkdownJsonFence(content);
  if (!stripped.startsWith('{')) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(stripped);
  } catch {
    return null;
  }

  if (!isRecord(parsed)) {
    return null;
  }

  const body = extractJsonPrimaryAnswer(parsed);
  if (!body) {
    return null;
  }

  const metaSections: AssistantMessageSection[] = [];

  const sourcesMd = formatSourcesMarkdown(parsed.sources);
  if (sourcesMd) {
    metaSections.push({ title: 'Sources', content: sourcesMd, i18nKey: 'sources' });
  }

  if (typeof parsed.confidence === 'string' && parsed.confidence.trim()) {
    metaSections.push({
      title: 'Confidence',
      content: parsed.confidence.trim(),
      i18nKey: 'confidence',
    });
  }

  const limitationsMd = formatStringArrayField(parsed.limitations);
  if (limitationsMd) {
    metaSections.push({ title: 'Limitations', content: limitationsMd, i18nKey: 'limitations' });
  }

  const recommendationsMd = formatStringArrayField(parsed.recommendations);
  if (recommendationsMd) {
    metaSections.push({
      title: 'Recommendations',
      content: recommendationsMd,
      i18nKey: 'recommendations',
    });
  }

  return {
    body,
    metaSections,
    mode: metaSections.length > 0 ? 'structured' : 'raw',
    parser: 'json',
  };
}

const normalizeSectionTitle = (title: string): string =>
  title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/** The i18n key for a recognized meta title, or undefined for sources/unknown. */
const metaI18nKey = (title: string): AssistantMessageSection['i18nKey'] | undefined => {
  const n = normalizeSectionTitle(title);
  if (SOURCE_SECTION_HINTS.some((h) => n.includes(h))) return 'sources';
  if (n.includes('confidence') || n.includes('confiance')) return 'confidence';
  if (n.includes('limitation')) return 'limitations';
  if (n.includes('recommendation') || n.includes('recommandation')) return 'recommendations';
  return undefined;
};

/** True only for titles we are willing to hide into the details modal. */
const isMetaTitle = (title: string): boolean => {
  const n = normalizeSectionTitle(title);
  return (
    SOURCE_SECTION_HINTS.some((h) => n.includes(h)) ||
    META_SECTION_HINTS.some((h) => n.includes(h))
  );
};

/**
 * Split a markdown answer into the visible body and any *recognized* meta
 * sections (Sources / Confidence / Limitations / Recommendations).
 *
 * Everything that is not a recognized meta section — intro prose, ordinary
 * headers such as "Résultat global:", "Par pôle:", "En bref:", bullet lists —
 * is preserved verbatim in the body, in original order. A meta section runs from
 * its header line to the next header or the next blank line, so it can't swallow
 * unrelated content that follows it.
 */
const splitBodyAndMeta = (
  content: string,
): { body: string; metaSections: AssistantMessageSection[] } => {
  const lines = content.split(/\r?\n/);
  const bodyLines: string[] = [];
  const metaSections: AssistantMessageSection[] = [];
  let current: { title: string; lines: string[] } | null = null;

  const flush = () => {
    if (!current) return;
    const sectionContent = current.lines.join('\n').trim();
    if (sectionContent) {
      metaSections.push({
        title: current.title,
        content: sectionContent,
        i18nKey: metaI18nKey(current.title),
      });
    }
    current = null;
  };

  for (const line of lines) {
    const blockMatch = line.match(HEADER_PATTERN);
    const inlineMatch = blockMatch ? null : line.match(INLINE_LINE_PATTERN);
    const headerTitle = blockMatch
      ? blockMatch[1].trim()
      : inlineMatch
        ? inlineMatch[1].trim()
        : null;

    if (headerTitle !== null && isMetaTitle(headerTitle)) {
      flush();
      current = { title: headerTitle, lines: [] };
      const inlineValue = inlineMatch ? inlineMatch[2].trim() : '';
      if (inlineValue) current.lines.push(inlineValue);
      continue;
    }

    if (current) {
      // A blank line or a new (non-meta) header ends the meta block; the line
      // itself belongs to the body.
      if (line.trim() === '' || headerTitle !== null) {
        flush();
        bodyLines.push(line);
        continue;
      }
      current.lines.push(line);
      continue;
    }

    bodyLines.push(line);
  }

  flush();

  return { body: bodyLines.join('\n').trim(), metaSections };
};

/**
 * Returns the best plain-text / markdown answer for copy, export, and evaluations.
 * If content is JSON with an `answer` field, returns that; otherwise the original string.
 */
export function getAssistantAnswerText(content: string): string {
  const jsonTry = tryParseJsonAssistantPayload(content);
  if (jsonTry) {
    return jsonTry.body;
  }
  return content;
}

export function parseAssistantContent(content: string): ParsedAssistantPayload {
  const jsonParsed = tryParseJsonAssistantPayload(content);
  if (jsonParsed) {
    return jsonParsed;
  }

  const { body, metaSections } = splitBodyAndMeta(content);

  // Only treat as structured when we peeled off at least one recognized meta
  // section AND real body content remains. Otherwise render the answer verbatim
  // so ordinary prose is never hidden or reordered.
  if (metaSections.length === 0 || !body) {
    return {
      body: content,
      metaSections: [],
      mode: 'raw',
      parser: 'none',
    };
  }

  return {
    body,
    metaSections,
    mode: 'structured',
    parser: 'block',
  };
}
